import { useState, useEffect } from 'react'
import Modal from '../Modal/Modal'
import API from '../../services/apiService'
import { Skeleton } from '../Skeleton/Skeleton'
import { useConfirm } from '../ConfirmModal/ConfirmModal'
import './TabsModal.css'

// Account Icon - Enhanced with premium gold gradient and shine
const AccountIcon = () => (
  <svg viewBox="0 0 64 64" width="44" height="44" className="tabs__account-icon">
    <defs>
      <linearGradient id="accountGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef3c7"/>
        <stop offset="30%" stopColor="#fde68a"/>
        <stop offset="60%" stopColor="#fbbf24"/>
        <stop offset="100%" stopColor="#f59e0b"/>
      </linearGradient>
      <radialGradient id="accountShine" cx="35%" cy="30%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.6)"/>
        <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
      </radialGradient>
      <filter id="accountShadow">
        <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodOpacity="0.4"/>
      </filter>
    </defs>
    <circle cx="32" cy="32" r="30" fill="#1e293b" opacity="0.2"/>
    <circle cx="32" cy="20" r="13" fill="url(#accountGold)" stroke="#92400e" strokeWidth="2.5" filter="url(#accountShadow)"/>
    <path d="M8 52 C8 34 16 28 32 28 C48 28 56 34 56 52" fill="url(#accountGold)" stroke="#92400e" strokeWidth="2.5" filter="url(#accountShadow)"/>
    <circle cx="32" cy="32" r="30" fill="url(#accountShine)" opacity="0.5"/>
  </svg>
)

// Tab Icon - Enhanced with 3D effect and detailed design
const TabIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" className="tabs__tab-icon">
    <defs>
      <linearGradient id="tabGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef3c7"/>
        <stop offset="40%" stopColor="#fde68a"/>
        <stop offset="70%" stopColor="#fbbf24"/>
        <stop offset="100%" stopColor="#f59e0b"/>
      </linearGradient>
      <filter id="tabShadow">
        <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodOpacity="0.4"/>
      </filter>
      <linearGradient id="tabHeader" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="rgba(0,0,0,0.1)"/>
        <stop offset="100%" stopColor="rgba(0,0,0,0.2)"/>
      </linearGradient>
    </defs>
    <rect x="2" y="4" width="20" height="16" rx="2.5" fill="url(#tabGold)" stroke="#92400e" strokeWidth="1.5" filter="url(#tabShadow)"/>
    <rect x="2" y="4" width="20" height="4" rx="2.5" fill="url(#tabHeader)"/>
    <circle cx="5" cy="6" r="0.9" fill="#ef4444" stroke="#7f1d1d" strokeWidth="0.3"/>
    <circle cx="8" cy="6" r="0.9" fill="#fbbf24" stroke="#78350f" strokeWidth="0.3"/>
    <circle cx="11" cy="6" r="0.9" fill="#10b981" stroke="#064e3b" strokeWidth="0.3"/>
    <line x1="5" y1="11" x2="19" y2="11" stroke="#92400e" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="5" y1="14" x2="16" y2="14" stroke="#92400e" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="5" y1="17" x2="13" y2="17" stroke="#92400e" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
)

// Tabs Summary Skeleton
const TabsSummarySkeleton = () => (
  <div className="tabs__summary-skeleton">
    <div className="tabs__summary-skeleton-card">
      <Skeleton width="44px" height="44px" borderRadius="8px" />
      <div className="tabs__summary-skeleton-info">
        <Skeleton width="80px" height="14px" />
        <Skeleton width="40px" height="24px" style={{ marginTop: '4px' }} />
      </div>
    </div>
    <div className="tabs__summary-skeleton-card">
      <Skeleton width="44px" height="44px" borderRadius="8px" />
      <div className="tabs__summary-skeleton-info">
        <Skeleton width="70px" height="14px" />
        <Skeleton width="30px" height="24px" style={{ marginTop: '4px' }} />
      </div>
    </div>
    <div className="tabs__summary-skeleton-card">
      <Skeleton width="44px" height="44px" borderRadius="8px" />
      <div className="tabs__summary-skeleton-info">
        <Skeleton width="75px" height="14px" />
        <Skeleton width="30px" height="24px" style={{ marginTop: '4px' }} />
      </div>
    </div>
  </div>
)

// Loading Spinner
const LoadingSpinner = ({ text = 'Loading...' }) => (
  <div className="tabs__loading">
    <div className="tabs__spinner"></div>
    <span>{text}</span>
  </div>
)

const TabsModal = ({ isOpen, onClose, client }) => {
  const [license, setLicense] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  
  // Add tabs form
  const [addTabsForm, setAddTabsForm] = useState({
    quantity: '',
    description: '',
    expiresAt: ''
  })
  const [showAddForm, setShowAddForm] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [tabType, setTabType] = useState('permanent')

  // Remove tabs form
  const [removeAmount, setRemoveAmount] = useState('')
  const [showRemoveForm, setShowRemoveForm] = useState(false)

  const confirm = useConfirm()

  // Edit newtab
  const [editingNewtab, setEditingNewtab] = useState(null)

  useEffect(() => {
    if (isOpen && client) {
      loadLicense()
    } else {
      resetState()
    }
  }, [isOpen, client])

  const resetState = () => {
    setLicense(null)
    setError(null)
    setSuccess(null)
    setShowAddForm(false)
    setShowRemoveForm(false)
    setEditingNewtab(null)
    setAddTabsForm({ quantity: '', description: '', expiresAt: '' })
    setRemoveAmount('')
  }

  const loadLicense = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await API.Licenses.getClientLicense(client.id)
      
      if (response.success) {
        setLicense(response.data)
      } else {
        setError(typeof response.error === 'object' ? response.error?.message || 'Failed to load license' : response.error)
      }
    } catch (err) {
      console.error('Failed to load license:', err)
      setError('Failed to load license data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddTabs = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!addTabsForm.quantity || addTabsForm.quantity < 1) {
      setError('Quantity must be at least 1')
      return
    }

    setAddLoading(true)

    try {
      const result = await API.Licenses.addTabs(client.id, parseInt(addTabsForm.quantity))
      
      if (result.success) {
        setSuccess(`✅ Added ${addTabsForm.quantity} permanent tabs successfully!`)
        setAddTabsForm({ quantity: '', description: '', expiresAt: '' })
        setShowAddForm(false)
        await loadLicense()
      } else {
        setError(typeof result.error === 'object' ? result.error?.message || 'Failed to add tabs' : result.error)
      }
    } catch (err) {
      setError('An error occurred while adding tabs')
    } finally {
      setAddLoading(false)
    }
  }

  const handleAddNewtab = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!addTabsForm.quantity || addTabsForm.quantity < 1) {
      setError('Quantity must be at least 1')
      return
    }

    if (!addTabsForm.expiresAt) {
      setError('Expiration date is required for temporary tabs')
      return
    }

    setAddLoading(true)

    try {
      const body = {
        quantity: parseInt(addTabsForm.quantity),
        expiresAt: new Date(addTabsForm.expiresAt).toISOString(),
        description: addTabsForm.description || 'Temporary tab package',
        notes: ''
      }

      const result = await API.Licenses.addNewTab(
        client.id,
        body.quantity,
        body.expiresAt,
        body.description,
        body.notes
      )
      
      if (result.success) {
        setSuccess(`✅ Added ${addTabsForm.quantity} temporary tabs successfully!`)
        setAddTabsForm({ quantity: '', description: '', expiresAt: '' })
        setShowAddForm(false)
        await loadLicense()
      } else {
        setError(typeof result.error === 'object' ? result.error?.message || 'Failed to add newtab' : result.error)
      }
    } catch (err) {
      setError('An error occurred while adding newtab')
    } finally {
      setAddLoading(false)
    }
  }

  const handleRemoveTabs = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!removeAmount || removeAmount < 1) {
      setError('Amount must be at least 1')
      return
    }

    if (!await confirm(`Remove ${removeAmount} permanent tabs from ${client.username}?`)) {
      return
    }

    try {
      const result = await API.Licenses.removeTabs(client.id, parseInt(removeAmount))
      
      if (result.success) {
        setSuccess(`✅ Removed ${removeAmount} permanent tabs successfully!`)
        setRemoveAmount('')
        setShowRemoveForm(false)
        await loadLicense()
      } else {
        setError(typeof result.error === 'object' ? result.error?.message || 'Failed to remove tabs' : result.error)
      }
    } catch (err) {
      setError('An error occurred while removing tabs')
    }
  }

  const handleDeleteNewtab = async (newtabId) => {
    if (!await confirm('Delete this temporary tab package?')) return

    try {
      const result = await API.Licenses.deleteNewTab(newtabId)
      
      if (result.success) {
        setSuccess('✅ Newtab package deleted successfully!')
        await loadLicense()
      } else {
        setError(typeof result.error === 'object' ? result.error?.message || 'Failed to delete newtab' : result.error)
      }
    } catch (err) {
      setError('An error occurred while deleting newtab')
    }
  }

  const handleUpdateNewtab = async (newtabId, updates) => {
    try {
      const result = await API.Licenses.updateNewTab(newtabId, updates)
      
      if (result.success) {
        setSuccess('✅ Newtab updated successfully!')
        setEditingNewtab(null)
        await loadLicense()
      } else {
        setError(typeof result.error === 'object' ? result.error?.message || 'Failed to update newtab' : result.error)
      }
    } catch (err) {
      setError('An error occurred while updating newtab')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTotalTabs = () => {
    if (!license) return 0
    return license.totalAvailableTabs || (license.tabs || 0)
  }

  const getPermanentTabs = () => {
    return license?.tabs || 0
  }

  const getTemporaryTabs = () => {
    if (!license?.newtabs || license.newtabs.length === 0) return 0
    return license.newtabs
      .filter(nt => !nt.isExpired)
      .reduce((sum, nt) => sum + (nt.quantity || 0), 0)
  }

  if (!client) return null

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Tabs - ${client.username}`}
      modalClassName="tabs-modal"
    >
      <div className="tabs">
        {/* Client Header */}
        <div className="tabs__header">
          <AccountIcon />
          <div className="tabs__client-info">
            <h3 className="tabs__username">{client.username}</h3>
            <span className="tabs__client-id">ID: {client.id}</span>
          </div>
        </div>

        {loading && <TabsSummarySkeleton />}

        {error && (
          <div className="tabs__error">
            <span>⚠️</span> {error}
          </div>
        )}

        {success && (
          <div className="tabs__success">
            {success}
          </div>
        )}

        {!loading && license && (
          <div className="tabs__content-loaded">
            {/* Tabs Summary */}
            <div className="tabs__summary">
              <div className="tabs__summary-card tabs__summary-card--total">
                <TabIcon />
                <div>
                  <span className="tabs__summary-label">Total Tabs</span>
                  <span className="tabs__summary-value">{getTotalTabs()}</span>
                </div>
              </div>
              <div className="tabs__summary-card tabs__summary-card--permanent">
                <span className="tabs__summary-icon">∞</span>
                <div>
                  <span className="tabs__summary-label">Permanent</span>
                  <span className="tabs__summary-value">{getPermanentTabs()}</span>
                </div>
              </div>
              <div className="tabs__summary-card tabs__summary-card--temporary">
                <span className="tabs__summary-icon">⏳</span>
                <div>
                  <span className="tabs__summary-label">Temporary</span>
                  <span className="tabs__summary-value">{getTemporaryTabs()}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="tabs__actions">
              <button 
                className="tabs__btn tabs__btn--add"
                onClick={() => {
                  setShowAddForm(!showAddForm)
                  setShowRemoveForm(false)
                  setError(null)
                  setSuccess(null)
                }}
              >
                ➕ Add Tabs
              </button>
              <button 
                className="tabs__btn tabs__btn--remove"
                onClick={() => {
                  setShowRemoveForm(!showRemoveForm)
                  setShowAddForm(false)
                  setError(null)
                  setSuccess(null)
                }}
                disabled={getPermanentTabs() === 0}
              >
                ➖ Remove Tabs
              </button>
            </div>

            {/* Add Tabs Form */}
            {showAddForm && (
              <div className="tabs__form-section">
                <h4>Add Tabs</h4>
                <div className="tabs__form-type-selector">
                  <label className="tabs__radio-label">
                    <input 
                      type="radio" 
                      name="tabType" 
                      value="permanent" 
                      checked={tabType === 'permanent'}
                      onChange={(e) => {
                        setTabType(e.target.value)
                        setAddTabsForm({ ...addTabsForm, expiresAt: '' })
                      }}
                    />
                    <span>Permanent (∞)</span>
                  </label>
                  <label className="tabs__radio-label">
                    <input 
                      type="radio" 
                      name="tabType" 
                      value="temporary"
                      checked={tabType === 'temporary'}
                      onChange={(e) => setTabType(e.target.value)}
                    />
                    <span>Temporary (⏳)</span>
                  </label>
                </div>
                
                <form onSubmit={(e) => {
                  tabType === 'temporary' ? handleAddNewtab(e) : handleAddTabs(e)
                }}>
                  <div className="tabs__form-field">
                    <label>Quantity</label>
                    <input 
                      type="number"
                      min="1"
                      value={addTabsForm.quantity}
                      onChange={(e) => setAddTabsForm({ ...addTabsForm, quantity: e.target.value })}
                      required
                      disabled={addLoading}
                    />
                  </div>

                  {tabType === 'temporary' && (
                    <>
                      <div className="tabs__form-field">
                        <label>Expires At</label>
                        <div className="tabs__date-shortcuts">
                          <button 
                            type="button"
                            className="tabs__shortcut-btn"
                            onClick={() => {
                              const date = new Date()
                              date.setMonth(date.getMonth() + 1)
                              setAddTabsForm({ 
                                ...addTabsForm, 
                                expiresAt: date.toISOString().slice(0, 16)
                              })
                            }}
                            disabled={addLoading}
                          >
                            +1 Month
                          </button>
                          <button 
                            type="button"
                            className="tabs__shortcut-btn"
                            onClick={() => {
                              const date = new Date()
                              date.setMonth(date.getMonth() + 6)
                              setAddTabsForm({ 
                                ...addTabsForm, 
                                expiresAt: date.toISOString().slice(0, 16)
                              })
                            }}
                            disabled={addLoading}
                          >
                            +6 Months
                          </button>
                        </div>
                        <input 
                          type="datetime-local"
                          value={addTabsForm.expiresAt}
                          onChange={(e) => setAddTabsForm({ ...addTabsForm, expiresAt: e.target.value })}
                          required
                          disabled={addLoading}
                        />
                      </div>
                      <div className="tabs__form-field">
                        <label>Description (Optional)</label>
                        <input 
                          type="text"
                          value={addTabsForm.description}
                          onChange={(e) => setAddTabsForm({ ...addTabsForm, description: e.target.value })}
                          placeholder="e.g., Monthly subscription"
                          disabled={addLoading}
                        />
                      </div>
                    </>
                  )}

                  <div className="tabs__form-buttons">
                    <button 
                      type="submit" 
                      className="tabs__btn tabs__btn--submit"
                      disabled={addLoading}
                    >
                      {addLoading ? (
                        <>
                          <div className="tabs__spinner-small"></div>
                          Adding...
                        </>
                      ) : (
                        'Add Tabs'
                      )}
                    </button>
                    <button 
                      type="button"
                      className="tabs__btn tabs__btn--cancel"
                      onClick={() => {
                        setShowAddForm(false)
                        setAddTabsForm({ quantity: '', description: '', expiresAt: '' })
                        setTabType('permanent')
                      }}
                      disabled={addLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Remove Tabs Form */}
            {showRemoveForm && (
              <div className="tabs__form-section">
                <h4>Remove Permanent Tabs</h4>
                <form onSubmit={handleRemoveTabs}>
                  <div className="tabs__form-field">
                    <label>Amount to Remove (Max: {getPermanentTabs()})</label>
                    <input 
                      type="number"
                      min="1"
                      max={getPermanentTabs()}
                      value={removeAmount}
                      onChange={(e) => setRemoveAmount(e.target.value)}
                      required
                    />
                  </div>
                  <div className="tabs__form-buttons">
                    <button type="submit" className="tabs__btn tabs__btn--submit">
                      Remove Tabs
                    </button>
                    <button 
                      type="button"
                      className="tabs__btn tabs__btn--cancel"
                      onClick={() => {
                        setShowRemoveForm(false)
                        setRemoveAmount('')
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Temporary Tabs List */}
            {license.newtabs && license.newtabs.length > 0 && (
              <div className="tabs__newtabs-section">
                <h4>Temporary Tab Packages</h4>
                <div className="tabs__newtabs-list">
                  {license.newtabs.map(newtab => (
                    <div 
                      key={newtab.id} 
                      className={`tabs__newtab-card ${newtab.isExpired ? 'tabs__newtab-card--expired' : ''}`}
                    >
                      <div className="tabs__newtab-info">
                        <div className="tabs__newtab-header">
                          <span className="tabs__newtab-quantity">
                            {newtab.quantity} tabs
                          </span>
                          {newtab.isExpired ? (
                            <span className="tabs__newtab-badge tabs__newtab-badge--expired">Expired</span>
                          ) : (
                            <span className="tabs__newtab-badge tabs__newtab-badge--active">Active</span>
                          )}
                        </div>
                        {newtab.description && (
                          <p className="tabs__newtab-description">{newtab.description}</p>
                        )}
                        <div className="tabs__newtab-dates">
                          <span>Expires: {formatDate(newtab.expiresAt)}</span>
                          {newtab.timeRemaining && !newtab.isExpired && (
                            <span className="tabs__newtab-time">⏱️ {newtab.timeRemaining}</span>
                          )}
                        </div>
                        {newtab.lastModifiedByAdminName && (
                          <div className="tabs__newtab-admin">
                            <span>👤 Added by: <strong>{newtab.lastModifiedByAdminName}</strong></span>
                          </div>
                        )}
                      </div>
                      <div className="tabs__newtab-actions">
                        <button 
                          className="tabs__btn tabs__btn--small tabs__btn--danger"
                          onClick={() => handleDeleteNewtab(newtab.id)}
                          title="Delete Package"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* License Info */}
            <div className="tabs__license-info">
              {license.lastModifiedByAdminName && (
                <div className="tabs__info-item">
                  <span className="tabs__info-label">Modified by:</span>
                  <span className="tabs__info-value tabs__info-value--admin">{license.lastModifiedByAdminName}</span>
                </div>
              )}
              <div className="tabs__info-item">
                <span className="tabs__info-label">Last Updated:</span>
                <span className="tabs__info-value">{formatDate(license.updatedAt)}</span>
              </div>
              <div className="tabs__info-item">
                <span className="tabs__info-label">Created:</span>
                <span className="tabs__info-value">{formatDate(license.createdAt)}</span>
              </div>
            </div>
          </div>
        )}

        {!loading && !license && !error && (
          <div className="tabs__empty">
            <p>No license found for this client.</p>
            <button 
              className="tabs__btn tabs__btn--primary"
              onClick={() => setShowAddForm(true)}
            >
              Create License with Tabs
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default TabsModal
